import { Helmet } from "react-helmet";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  structuredData?: object;
}

export default function SEO({
  title = "Unpatterned - Research, Infrastructure, and Knowledge Network",
  description = "An independent research, infrastructure, and knowledge network building open systems for how the world is represented in data, identity, and intelligence.",
  keywords = [
    "open infrastructure",
    "digital public infrastructure",
    "DPI",
    "open source AI",
    "digital identity",
    "OpenStreetMap",
    "data infrastructure",
    "AI infrastructure",
    "emerging economies",
    "public interest technology",
    "open data",
    "representation infrastructure",
    "post-quantum cryptography",
    "vector databases",
    "knowledge graphs",
    "hyperdimensional computing",
    "HDC",
    "world models",
    "robotics",
    "agent infrastructure",
    "design research",
    "representation systems",
    "non-profit research lab",
    "community infrastructure",
    "Global South technology",
    "Africa tech infrastructure",
    "open contribution",
    "MOSIP",
    "Masakhane",
    "W3C",
    "IETF",
    "permissive licensing"
  ],
  image = "https://unpatterned.org/og-image.png",
  url = "https://unpatterned.org",
  type = "website",
  author = "Unpatterned",
  publishedTime,
  structuredData,
}: SEOProps) {
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "ResearchOrganization",
    "name": "Unpatterned",
    "alternateName": "Unpatterned Labs",
    "description": "An independent research, infrastructure, and knowledge network building open systems for how the world is represented in data, identity, and intelligence.",
    "url": "https://unpatterned.org",
    "logo": "https://unpatterned.org/logo.png",
    "foundingDate": "2024",
    "nonprofitStatus": "NonprofitType",
    "sameAs": [
      "https://linkedin.com/company/unpatterned",
      "https://theunpatterned.substack.com",
      "https://github.com/unpatterned-labs"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "General Inquiries",
      "url": "https://unpatterned.org"
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "Nigeria"
      },
      {
        "@type": "Country",
        "name": "Kenya"
      },
      {
        "@type": "Country",
        "name": "Ghana"
      },
      {
        "@type": "Country",
        "name": "South Africa"
      },
      {
        "@type": "Country",
        "name": "India"
      },
      {
        "@type": "Place",
        "name": "Global"
      }
    ],
    "knowsAbout": [
      "Open Source Infrastructure",
      "Digital Public Infrastructure",
      "AI Infrastructure",
      "Digital Identity",
      "Data Systems",
      "Representation Infrastructure",
      "Post-Quantum Cryptography",
      "Knowledge Graphs",
      "Hyperdimensional Computing",
      "Agent Infrastructure",
      "OpenStreetMap",
      "Representation Systems"
    ],
    "sponsor": {
      "@type": "Organization",
      "name": "OpenStreetMap Foundation"
    }
  };

  // Import the logo for favicon
  const faviconUrl = "https://unpatterned.org/favicon.ico";
  const iconUrl = "https://unpatterned.org/icon.png";
  const appleTouchIconUrl = "https://unpatterned.org/apple-touch-icon.png";

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Favicons and Icons */}
      <link rel="icon" type="image/x-icon" href={faviconUrl} />
      <link rel="icon" type="image/png" sizes="32x32" href={iconUrl} />
      <link rel="icon" type="image/png" sizes="16x16" href={iconUrl} />
      <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIconUrl} />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="msapplication-TileColor" content="#184b9d" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Unpatterned" />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO */}
      <meta name="theme-color" content="#184b9d" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Unpatterned" />
      
      {/* Enhanced Geo Tags for Global South Focus */}
      <meta name="geo.region" content="NG;KE;GH;ZA;US;GB;IN" />
      <meta name="geo.placename" content="Lagos, Nairobi, Accra, Cape Town, Global" />
      <meta name="ICBM" content="6.5244,3.3792" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
    </Helmet>
  );
}
